import { Button } from "@/components/ui/button";
import { getMainLanguage, LocaleDict, SupportedLocale } from "@/i18n";
import {
  captionAlignmentToTextAlignment,
  LayoutNonNullable,
  LayoutNumberOfColumns,
  Section,
  SectionDescriptionVisibility,
  Selection,
} from "@/lib/cms/section-types";
import { DimensionsFormatting } from "@/lib/compute-field/shared/dimensions";
import { InscriptionDosCF } from "@/lib/compute-field/shared/inscription-dos";
import { RelatedAuthorsListCF } from "@/lib/compute-field/shared/related-authors";
import { SignatureCF } from "@/lib/compute-field/shared/signature";
import { SupportTechniqueCF } from "@/lib/compute-field/shared/technique";
import { DateCF, MainTitleCF } from "@/lib/compute-field/shared/title";
import { SeparatedList } from "@/lib/compute-field/shared/utils";
import { AuthorCoreInfo, Dimensions, ImageSize } from "@/lib/types";
import {
  chunkArray,
  chunkArrayVertically,
  cn,
  getImageSize,
  getPaddingToApplyToCenter,
} from "@/lib/utils";
import { useEffect, useMemo, useRef, useState } from "react";
import { isMobile } from "react-device-detect";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

type DisplayableTypeMap = {
  section: Section;
  selection: Selection;
};

type SupportedDataTypes = DisplayableTypeMap[keyof DisplayableTypeMap];

type GridDisplayProps = {
  [K in keyof DisplayableTypeMap]: {
    displayable: {
      dType: K;
      data: DisplayableTypeMap[K][];
    };
    layout: LayoutNonNullable;
  } & (K extends "selection"
    ? { visibility: SectionDescriptionVisibility }
    : { visibility?: never });
}[keyof DisplayableTypeMap];

export const GridDisplay = ({
  displayable,
  layout,
  visibility,
}: GridDisplayProps) => {
  const { data, dType } = displayable;

  const dataChunks = useMemo(
    () =>
      layout.alignment !== "SPACE_AROUND"
        ? chunkArray<SupportedDataTypes>(data, layout.numberOfColumns)
        : chunkArrayVertically<SupportedDataTypes>(
            data,
            layout.numberOfColumns,
          ),
    [data, layout],
  );

  return (
    <div
      className={cn(
        `w-full flex gap-8 ${layout.alignment === "SPACE_AROUND" ? "flex-wrap" : "flex-col"} `,
      )}
    >
      {(() => {
        switch (dType) {
          case "section":
            return dataChunks.map((chunk, index) => (
              <RowDisplaySection
                key={index}
                layout={layout}
                rowData={chunk as Section[]}
              />
            ));
          case "selection":
            return dataChunks.map((chunk, index) => (
              <RowDisplaySelection
                key={index}
                layout={layout}
                visibility={visibility!}
                rowData={chunk as Selection[]}
              />
            ));
        }
      })()}
    </div>
  );
};

type RowDisplaySelectionProps = {
  layout: LayoutNonNullable;
  visibility: SectionDescriptionVisibility;
  rowData: Selection[];
};

const RowDisplaySelection = ({
  rowData,
  layout,
  visibility,
}: RowDisplaySelectionProps) => {
  const {
    i18n: { language },
  } = useTranslation();

  return (
    <RowDisplay
      data={rowData}
      dType="selection"
      layout={layout}
      render={({ item, layout, paddingTop }) => {
        return (
          <div
            key={item.item.id}
            className="flex-col justify-start items-center inline-flex relative"
            style={{
              width: `${layout.alignment === "SPACE_AROUND" ? 100 : getMaxWidthPercent(isMobile, layout.numberOfColumns)}%`,
            }}
          >
            <div
              style={{
                paddingTop,
              }}
            >
              <img className="relative" src={item.item.url ?? ""} />
            </div>
            <div
              className={`flex flex-col w-full py-2 gap-0.5 text-${layout.captionAlignment === "LEFT" ? "left" : layout.captionAlignment === "CENTER" ? "center" : "right"}`}
            >
              {item.item.imageType === "artwork" && (
                <SelectionInfoDisplay
                  item={{
                    title: item.item.oeuvresDescriptionTitre,
                    unknownTitle: item.item.oeuvresDescriptionTitreInconnu,
                    noTitle: item.item.oeuvresDescriptionTitreSansTitre,
                    unconfirmedTitle:
                      item.item.oeuvresValidationTitleNotConfirmed,
                    authors: item.item.relatedAuthors,
                    date: item.item.oeuvresDescriptionDateDeCreation,
                    dateUnknown:
                      item.item.oeuvresDescriptionDateDeCreationInconnue,
                    technique: item.item.oeuvresDescriptionTechnique,
                    support: item.item.oeuvresDescriptionSupport,
                    doubleFaced: item.item.oeuvresDescriptionOeuvreDoubleFace,
                    dimensions: item.item.oeuvresDescriptionDimensionsOeuvre,
                    dimensionsUnknown:
                      item.item.oeuvresDescriptionDimensionsOeuvreInconnues,
                    backText:
                      item.item.oeuvresDescriptionInscriptionsDosMultilingual,
                    signatureLocalisation:
                      item.item.oeuvresDescriptionSignatureLocalisation,
                    signatureTranscription:
                      item.item.oeuvresDescriptionSignatureTranscription,
                    registrationNumber:
                      item.item.oeuvresDescriptionNumeroEnregistrement,
                    collection:
                      item.item
                        .oeuvresLocalisationHistoriqueCollectionCatalogue,
                  }}
                  visibility={visibility}
                />
              )}

              {item.item.imageType === "multipleprint" && (
                <SelectionInfoDisplay
                  item={{
                    title: item.item.estampesMultiplesDescriptionTitre,
                    unknownTitle:
                      item.item.estampesMultiplesDescriptionTitreInconnu,
                    noTitle:
                      item.item.estampesMultiplesDescriptionTitreSansTitre,
                    unconfirmedTitle:
                      item.item.estampesMultiplesDescriptionTitreIncertin,
                    authors: item.item.relatedAuthors,
                    date: item.item.estampesMultiplesDescriptionDateDeCreation,
                    dateUnknown:
                      item.item
                        .estampesMultiplesDescriptionDateDeCreationInconnue,
                    technique: item.item.estampesMultiplesDescriptionTechnique,
                    support:
                      item.item.estampesMultiplesDescriptionSupportMateriau,
                    doubleFaced:
                      item.item.estampesMultiplesDescriptionOeuvreDoubleFace,
                    dimensions:
                      item.item.estampesMultiplesDescriptionDimensionsOeuvre,
                    dimensionsUnknown:
                      item.item
                        .estampesMultiplesDescriptionDimensionsOeuvreInconnues,
                    backText:
                      item.item.estampesMultiplesDescriptionInscriptionsDos,
                    signatureLocalisation:
                      item.item
                        .estampesMultiplesDescriptionSignatureLocalisation,
                    signatureTranscription:
                      item.item
                        .estampesMultiplesDescriptionSignatureTranscription,
                    registrationNumber:
                      item.item
                        .estampesMultiplesDescriptionNumeroEnregistrement,
                  }}
                  visibility={visibility}
                />
              )}

              {visibility.visibleCustomField &&
                item.caption?.[language as SupportedLocale]?.content && (
                  <p
                    className="text-xs pt-3"
                    dangerouslySetInnerHTML={{
                      __html: item.caption?.[getMainLanguage()]?.content ?? "",
                    }}
                  ></p>
                )}
            </div>
          </div>
        );
      }}
    />
  );
};

type SelectionInfoDisplayProps = {
  item: {
    title: string;
    unknownTitle: boolean;
    noTitle: boolean;
    unconfirmedTitle: boolean;
    authors: AuthorCoreInfo[];
    date: string;
    dateUnknown: boolean;
    technique: LocaleDict<string>;
    support: LocaleDict<string>;
    doubleFaced: boolean;
    dimensions: Dimensions;
    dimensionsUnknown: boolean;
    backText: LocaleDict<string>;
    signatureLocalisation: LocaleDict<string>;
    signatureTranscription: string;
    registrationNumber: string;
    collection?: string;
  };
  visibility: SectionDescriptionVisibility;
};

const SelectionInfoDisplay = ({
  item,
  visibility,
}: SelectionInfoDisplayProps) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  return (
    <>
      {visibility.visibleRegisterNumber && item.registrationNumber && (
        <p className="text-xs pb-3">
          <span className="text-gray-600">
            {t("registrationNumber")}
            {" : "}
          </span>
          <span className="font-semibold text-gray-950">
            {item.registrationNumber}
          </span>
        </p>
      )}

      {visibility.visibleAuthors && item.authors?.[0] && (
        <p className="text-xs">
          <RelatedAuthorsListCF
            input={{
              relatedAuthors: item.authors,
              attributedTo: false,
              from: false,
              tasteOf: false,
              entourageOf: false,
              studioOf: false,
            }}
            language={language as SupportedLocale}
          />
        </p>
      )}

      {(visibility.visibleTitles || visibility.visibleDates) &&
        (item.title || item.date || item.dateUnknown) && (
          <p>
            <SeparatedList
              language={language as SupportedLocale}
              commaSeparated
              lineBreak={false}
              input={[
                visibility.visibleTitles && (
                  <b>
                    <MainTitleCF
                      input={{
                        title: item.title,
                        titleState: {
                          notConfirmed: item.unconfirmedTitle,
                          none: item.noTitle,
                          unknown: item.unknownTitle,
                        },
                      }}
                      language={language as SupportedLocale}
                    />
                  </b>
                ),
                visibility.visibleDates && (
                  <DateCF
                    input={{
                      date: item.date,
                      unknownDate: item.dateUnknown,
                    }}
                    language={language as SupportedLocale}
                  />
                ),
              ]}
            />
          </p>
        )}

      {visibility.visibleDimensions && item.dimensions && (
        <p className="text-xs">
          <DimensionsFormatting
            input={{
              ...item.dimensions,
              unknownDimensions: item.dimensionsUnknown,
            }}
            language={language as SupportedLocale}
          />
        </p>
      )}

      {visibility.visibleTechniqueAndSupport &&
        (item.support?.[language as SupportedLocale] ||
          item.technique?.[language as SupportedLocale] ||
          item.doubleFaced) && (
          <p className="text-xs">
            <SupportTechniqueCF
              input={{
                technique: item.technique,
                support: item.support,
                isDoubleSided: item.doubleFaced,
              }}
              language={language as SupportedLocale}
            />
          </p>
        )}

      {visibility.visibleSignature &&
        (item.signatureLocalisation?.[language as SupportedLocale] ||
          item.signatureTranscription) && (
          <p className="text-xs">
            <SignatureCF
              input={{
                location: item.signatureLocalisation,
                transcription: item.signatureTranscription,
              }}
              language={language as SupportedLocale}
            />
          </p>
        )}

      {visibility.visibleBackText &&
        item.backText?.[language as SupportedLocale] && (
          <p className="text-xs">
            <InscriptionDosCF
              input={{
                inscription: item.backText,
              }}
              language={language as SupportedLocale}
            />
          </p>
        )}
      {visibility.visibleCurrentCollection && item.collection && (
        <p className="text-xs">{item.collection}</p>
      )}
    </>
  );
};

type RowDisplaySectionProps = {
  layout: LayoutNonNullable;
  rowData: Section[];
};

const RowDisplaySection = ({ rowData, layout }: RowDisplaySectionProps) => {
  const { t } = useTranslation();

  return (
    <RowDisplay
      data={rowData}
      dType="section"
      layout={layout}
      render={({ item, layout, paddingTop }) => (
        <div
          key={item.id}
          className="flex-col justify-start items-center inline-flex relative"
          style={{
            width: `${layout.alignment === "SPACE_AROUND" ? 100 : getMaxWidthPercent(isMobile, layout.numberOfColumns)}%`,
          }}
        >
          <div
            style={{
              paddingTop,
            }}
            className={`w-full`}
          >
            <img
              className="relative"
              src={item.thumbnail?.mediumPublicUrl ?? ""}
              alt={item.content?.[getMainLanguage()]?.title ?? ""}
            />
          </div>
          <div
            className={`flex-col w-full gap-1`}
            style={{
              textAlign: captionAlignmentToTextAlignment(
                layout.captionAlignment,
              ),
            }}
          >
            <div className="self-stretch py-2 flex-col justify-start items-start gap-1 flex">
              <span
                className="text-[#090909] text-base font-semibold font-['Instrument Sans'] leading-[14.40px] w-full"
                style={{
                  textAlign: captionAlignmentToTextAlignment(
                    layout.captionAlignment,
                  ),
                }}
              >
                {item.content?.[getMainLanguage()]?.title}
              </span>
              <div
                className="text-[#090909] text-xs w-full font-normal font-['Instrument Sans'] leading-[14.40px]"
                style={{
                  textAlign: captionAlignmentToTextAlignment(
                    layout.captionAlignment,
                  ),
                }}
                dangerouslySetInnerHTML={{
                  __html: item.content?.[getMainLanguage()]?.content ?? "",
                }}
              />
            </div>
            <Link to={`/cms/selection/section/${item.id}`}>
              <Button
                variant="outline"
                className="flex max-w-fit overflow-hidden"
              >
                {t("cms.selection.manageSection.button")}
              </Button>
            </Link>
          </div>
        </div>
      )}
    />
  );
};

type RowDisplayProps<K extends keyof DisplayableTypeMap> = {
  data: DisplayableTypeMap[K][];
  dType: K;
  layout: LayoutNonNullable;
  render: (props: {
    item: DisplayableTypeMap[K];
    layout: LayoutNonNullable;
    paddingTop: string;
  }) => JSX.Element;
};

const RowDisplay = <K extends keyof DisplayableTypeMap>({
  data,
  dType,
  layout,
  render,
}: RowDisplayProps<K>) => {
  const { numberOfColumns, alignment } = layout;
  const ref = useRef<HTMLDivElement>(null);

  const [sizes, setSizes] = useState<ImageSize[]>(() =>
    new Array(data.length).fill({ width: 0, height: 0 }),
  );

  useEffect(() => {
    const getUrls = () => {
      switch (dType) {
        case "section":
          return (data as Section[]).map(
            (d) => d.thumbnail?.mediumPublicUrl ?? "",
          );
        case "selection":
          return (data as Selection[]).map((d) => d.item.url ?? "");
        default:
          throw new Error("Unmanaged dType");
      }
    };

    const rowUrls = getUrls();

    Promise.all(rowUrls.map(getImageSize)).then((sizes) => setSizes(sizes));
  }, [data, dType]);

  return (
    <div
      className={cn(
        `w-full flex gap-8 ${alignment === "SPACE_AROUND" ? "flex-col" : "flex-wrap"}`,
      )}
      style={{
        width: `${alignment === "SPACE_AROUND" ? getMaxWidthPercent(isMobile, numberOfColumns) : 100}%`,
      }}
      ref={ref}
    >
      {data.map((item, index) => {
        const paddingTop = `${alignment === "CENTER" ? getPaddingToApplyToCenter(index, sizes, ref.current?.offsetWidth ? (ref.current.offsetWidth * getMaxWidthPercent(isMobile, layout?.numberOfColumns)) / 100 : 0) : 0}px`;
        return render({ item, layout, paddingTop });
      })}
    </div>
  );
};

const getMaxWidthPercent = (
  isMobile: boolean,
  numberOfColumns: LayoutNumberOfColumns,
) => {
  return isMobile
    ? 100
    : numberOfColumns === 2
      ? 45
      : numberOfColumns === 3
        ? 30
        : numberOfColumns === 4
          ? 22
          : 30;
};
